/**
 * Request access confirmation modal (catalog tools).
 * Matches ConfirmModal / Garage modal patterns.
 */

import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { X, Lock } from 'lucide-react';
import '../AIGarage.css';

const RequestAccessModal = ({
  isOpen,
  tool,
  onConfirm,
  onCancel,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setSubmitting(false);
      setError(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape' && !submitting) onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = 'unset';
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onCancel, submitting]);

  if (!isOpen || !tool) return null;

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm(tool);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Failed to submit request';
      setError(detail);
      setSubmitting(false);
    }
  };

  const persona = (tool.persona || 'GENERAL').toUpperCase();
  const typeLabel = tool.toolType === 'Gem' ? 'GEM' : 'APP';

  return ReactDOM.createPortal(
    <div className="garage-modal-overlay" onClick={() => !submitting && onCancel()}>
      <div
        className="garage-modal garage-confirm-modal garage-access-request-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="garage-access-request-title"
      >
        <div className="garage-modal-header">
          <div className="garage-modal-title-row">
            <div className="garage-modal-icon garage-access-request-modal-icon">
              <Lock size={22} />
            </div>
            <h2 id="garage-access-request-title">Request access</h2>
          </div>
          <button
            className="garage-modal-close"
            onClick={onCancel}
            disabled={submitting}
            aria-label="Close"
            type="button"
          >
            <X size={20} />
          </button>
        </div>

        <div className="garage-modal-body">
          <p className="garage-confirm-message">
            You don&apos;t currently have access to this app. Submitting a request notifies
            admins, who will review and provision access.
          </p>

          <div className="garage-access-request-app-info">
            <div className="garage-access-request-app-title">{tool.title}</div>
            <div className="garage-access-request-app-meta">
              {persona} · {typeLabel}
            </div>
          </div>

          <div className="garage-access-request-note" role="note">
            Track status under <strong>Access requests</strong>. Typical response time is 1–2
            business days.
          </div>

          {error && (
            <p className="garage-access-request-error" role="alert">
              {error}
            </p>
          )}
        </div>

        <div className="garage-modal-actions">
          <button
            type="button"
            className="garage-btn garage-btn-secondary"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="garage-btn garage-btn-primary"
            onClick={handleConfirm}
            disabled={submitting}
          >
            {submitting ? 'Submitting…' : 'Submit request'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RequestAccessModal;
